import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { AiOutlineDelete } from "react-icons/ai";
import { Header, ModelInfo } from "build-ts/metadata";
import { MetadataTypeEnum } from "build-ts/enums";
import { useUser } from "context/UserContext";
import { FiCalendar, FiClock, FiUser } from "react-icons/fi";
import { MdBarChart, MdNumbers } from "react-icons/md";
import { FaChartPie, FaCube } from "react-icons/fa";
import { PiTarget } from "react-icons/pi";
import { BiSolidNetworkChart } from "react-icons/bi";
import { TbMathFunction } from "react-icons/tb";
import { IoIosOptions } from "react-icons/io";
import ConfigurationListFilterControl from "./ConfigurationListFilterControl";
import { epochConverterWithTimeZone } from "utils/dateUtils";

interface ModelListProps {
  listModel: Header[];
  handleOpenModelEditor: (id: string) => void;
  onOpenThoughtspotFormula: (modelId: string) => void;
  deleteModal: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
  };
  confirmDelete: () => void;
  setDeleteModelId: React.Dispatch<React.SetStateAction<string | null>>;
  dependentMetricHeaders: Header[];
  setDependentMetricHeaders: React.Dispatch<React.SetStateAction<Header[]>>;
  modelInfoMap: Map<string, ModelInfo>;
  /** Map<modelId, Map<logicalColumnId, dependent Header[]>> */
  dependencyMap: Map<string, Map<string, Header[]>>;
  loading: boolean;
  attributeKgIds: Set<string>;
  measureKgIds: Set<string>;
  temporalKgIds: Set<string>;
}

/** 32–36px ring: gray empty, amber partial, green full. */
function MappingProgressRing({
  tooltipLabel,
  size = 34,
  strokeWidth = 6,
  progressPct,
}: {
  tooltipLabel: string;
  size?: number;
  strokeWidth?: number;
  progressPct: number;
}) {
  const r = (size - strokeWidth) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference - (progressPct / 100) * circumference;
  const progressStroke = progressPct === 100 ? "#38A169" : progressPct > 0 ? "#D69E2E" : "#A0AEC0";

  return (
    <Tooltip label={tooltipLabel} hasArrow openDelay={300} bg="gray.700" color="white" fontSize="xs">
      <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0} aria-label={tooltipLabel}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }} aria-hidden>
          <circle cx={c} cy={c} r={r} fill="none" stroke="#E2E8F0" strokeWidth={strokeWidth} />
          <circle
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={progressStroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 0.25s ease, stroke 0.2s ease" }}
          />
        </svg>
      </Box>
    </Tooltip>
  );
}

const ENTITY_TOOLTIP_MAX = 25;

function HoverEntityList({ title, items }: { title: string; items: string[] }) {
  const shown = items.slice(0, ENTITY_TOOLTIP_MAX);
  const rest = items.length - shown.length;
  return (
    <Box maxW="280px" maxH="220px" overflowY="auto" py={0.5}>
      <Text fontWeight="700" fontSize="xs" mb={1} color="white">
        {title}
      </Text>
      {shown.map((line, i) => (
        <Text key={i} fontSize="xs" color="whiteAlpha.900" mb={0} lineHeight="1.35">
          • {line}
        </Text>
      ))}
      {rest > 0 && (
        <Text fontSize="xs" color="whiteAlpha.700" mt={1} mb={0}>
          …and {rest} more
        </Text>
      )}
    </Box>
  );
}

function CompactStatusChip({
  icon,
  value,
  tooltipTitle,
  names,
  ariaLabel,
}: {
  icon: React.ElementType;
  value: number;
  tooltipTitle: string;
  names: (string | undefined)[];
  ariaLabel: string;
}) {
  return (
    <Tooltip
      label={<HoverEntityList title={tooltipTitle} items={names.filter((name): name is string => name !== undefined)} />}
      hasArrow
      openDelay={250}
      placement="top"
      bg="gray.700"
    >
      <HStack
        spacing={tooltipTitle === "Metrics" || tooltipTitle === "Metric" ? 1.5 : 1}
        as="span"
        align="center"
        py={1}
        px={2}
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.200"
        bg="gray.50"
        flexShrink={0}
        cursor="default"
        color="gray.400"
        _hover={{ color: "gray.700", cursor: "pointer" }}
        aria-label={`${ariaLabel}: ${value}`}
      >
        <Icon as={icon} boxSize={4} aria-hidden flexShrink={0} />
        <Text fontSize="sm" fontWeight="700" mb={0} lineHeight="1">
          {value}
        </Text>
        {(tooltipTitle === "Metrics" || tooltipTitle === "Metric") && (
          <Text fontSize="sm" fontWeight="600" mb={0} lineHeight="1">
            {tooltipTitle}
          </Text>
        )}
      </HStack>
    </Tooltip>
  );
}

const ModelList: React.FC<ModelListProps> = ({
  listModel,
  handleOpenModelEditor,
  onOpenThoughtspotFormula,
  deleteModal,
  confirmDelete,
  setDeleteModelId,
  dependentMetricHeaders,
  setDependentMetricHeaders,
  modelInfoMap,
  dependencyMap,
  loading,
  attributeKgIds,
  measureKgIds,
  temporalKgIds,
}) => {
  const { defaultTimeZone } = useUser();
  const [sortedModels, setSortedModels] = useState<Header[]>([]);

  return (
    <Box overflowY="auto" height="100vh" px={{ base: 3, md: 5 }} py={4}>
      <VStack align="stretch" spacing={5}>
        <HStack justify="space-between" align="center" w="full" minW={0} spacing={2} mx={1}>
          <HStack spacing={2} flexShrink={0} minW={0}>
            <Icon as={FaCube} boxSize={6} color="gray.700" flexShrink={0} aria-hidden />
            <Text fontSize="md" fontWeight="700" color="gray.700" letterSpacing="tight" mb={0}>
              Data models
            </Text>
          </HStack>
          <Box flex="1" minW={2} height="1px" bg="gray.300" mx={1} />
          <ConfigurationListFilterControl
            headerList={listModel}
            type="data_model"
            searchQuery=""
            splitDetailOpen={false}
            onChange={setSortedModels}
            defaultSortOption="created_desc"
          />
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
          {listModel?.length ? (
            sortedModels.map((model) => {
              const id = model.id!;
              const modelInfo = modelInfoMap.get(id);
              const logicalDepMap = dependencyMap.get(id) ?? new Map<string, Header[]>();

              const displayName = model.name || "Untitled model";
              const author = model.author_name?.trim() || "Unknown author";
              const dateOnly = model.last_modified_date_ms
                ? epochConverterWithTimeZone(model.last_modified_date_ms, "d MMM YYYY", defaultTimeZone)
                : "";
              const totalCols = modelInfo?.columns.length ?? 0;
              const canOpenFormulas = Boolean(modelInfo?.data_source?.ts_worksheet_id);

              // List of dependency headers for the model
              let mappedKgCount = 0;
              const metricHeaders: Header[] = [];
              const benchmarkHeaders: Header[] = [];
              const attributeHeaders: Header[] = [];
              const measureHeaders: Header[] = [];
              const temporalHeaders: Header[] = [];

              for (const headers of Array.from(logicalDepMap.values())) {
                let hasKg = false;
                for (const h of headers) {
                  if (h.type === MetadataTypeEnum.KG_COLUMN && h.id) {
                    hasKg = true;
                    if (attributeKgIds.has(h.id)) attributeHeaders.push(h);
                    else if (measureKgIds.has(h.id)) measureHeaders.push(h);
                    else if (temporalKgIds.has(h.id)) temporalHeaders.push(h);
                  } else if (h.type === MetadataTypeEnum.METRIC && h.id) {
                    metricHeaders.push(h);
                  } else if (h.type === MetadataTypeEnum.BENCHMARK && h.id) {
                    benchmarkHeaders.push(h);
                  }
                }
                if (hasKg) mappedKgCount++;
              }

              const progressPct = totalCols > 0 ? Math.min(100, Math.round((mappedKgCount / totalCols) * 100)) : 0;
              const ringTooltip =
                totalCols > 0
                  ? `Ontology progress: ${mappedKgCount} of ${totalCols} worksheet columns mapped to KG (${progressPct}%). ${metricHeaders.length} metrics · ${benchmarkHeaders.length} benchmarks.`
                  : `${mappedKgCount} columns mapped to KG · ${metricHeaders.length} metrics · ${benchmarkHeaders.length} benchmarks.`;

              return (
                <Card
                  key={id}
                  variant="outline"
                  borderRadius="lg"
                  boxShadow="md"
                  bg="white"
                  overflow="hidden"
                  display="flex"
                  flexDirection="column"
                  borderColor="gray.400"
                  _hover={{ boxShadow: "md", borderColor: "gray.400" }}
                  transition="box-shadow 0.15s ease, border-color 0.15s ease"
                >
                  <CardBody p={3} flex="1">
                    <Flex direction="column" gap={1.5} h="100%">
                      {/* Title + actions menu */}
                      <Flex align="center" justify="space-between" gap={2} w="100%">
                        <Text
                          flex={1}
                          minW={0}
                          fontSize="md"
                          fontWeight="650"
                          color="gray.900"
                          noOfLines={2}
                          mb={0}
                          textAlign="left"
                          cursor="pointer"
                          onClick={() => handleOpenModelEditor(id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleOpenModelEditor(id);
                            }
                          }}
                        >
                          {displayName}
                        </Text>
                        <Menu placement="bottom-start" strategy="fixed" closeOnSelect>
                          <MenuButton
                            as={IconButton}
                            aria-label="Model actions"
                            icon={<Icon as={IoIosOptions} boxSize={6} />}
                            variant="ghost"
                            size="sm"
                            color="purple.700"
                            flexShrink={0}
                            _hover={{ bg: "purple.50", borderColor: "purple.300" }}
                            _active={{ bg: "purple.100" }}
                            alignSelf="flex-start"
                          />
                          <MenuList zIndex={20} fontSize="md" minW="200px">
                            <MenuItem
                              icon={<Icon as={TbMathFunction} boxSize={5} />}
                              isDisabled={!canOpenFormulas || loading}
                              onClick={() => onOpenThoughtspotFormula(id)}
                              fontWeight="600"
                            >
                              Edit Model
                            </MenuItem>
                            <MenuItem
                              icon={<Icon as={BiSolidNetworkChart} boxSize={5} />}
                              onClick={() => handleOpenModelEditor(id)}
                              fontWeight="600"
                            >
                              Map to Ontology
                            </MenuItem>
                            <MenuItem
                              icon={<Icon as={AiOutlineDelete} boxSize={5} />}
                              color="red.600"
                              onClick={() => {
                                setDeleteModelId(id);
                                deleteModal.onOpen();
                              }}
                              fontWeight="600"
                            >
                              Delete model
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Flex>

                      {/* Date + author */}
                      <Flex w="100%" justify="space-between">
                        <HStack
                          spacing={2}
                          flexWrap="wrap"
                          justify="space-between"
                          maxW="100%"
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="500"
                          w="100%"
                        >
                          <HStack spacing={1} minW={0}>
                            <Icon as={FiClock} boxSize={3} color="gray.400" flexShrink={0} aria-hidden />
                            <Text noOfLines={1} mb={0}>
                              {dateOnly}
                            </Text>
                          </HStack>
                          <HStack spacing={1} minW={0}>
                            <Icon as={FiUser} boxSize={3} color="gray.400" flexShrink={0} aria-hidden />
                            <Text noOfLines={1} mb={0}>
                              {author}
                            </Text>
                          </HStack>
                        </HStack>
                      </Flex>

                      {/* Progress ring */}
                      {!loading ? (
                        <Flex align="center" gap={3} w="100%" py={0.5}>
                          <MappingProgressRing
                            tooltipLabel={ringTooltip}
                            progressPct={progressPct}
                          />
                          <VStack align="start" spacing={0} flex={1} minW={0}>
                            <Text fontSize="xs" fontWeight="600" color="gray.700" mb={0} lineHeight="short">
                              {`${mappedKgCount} / ${totalCols} columns in ontology`}
                            </Text>
                          </VStack>
                        </Flex>
                      ) : (
                        <HStack spacing={3} align="center" py={0.5} w="100%">
                          <Skeleton width="34px" height="34px" borderRadius="full" flexShrink={0} />
                          <Skeleton height="16px" flex={1} maxW="240px" borderRadius="sm" />
                        </HStack>
                      )}

                      {/* Stat chips */}
                      <Box cursor="pointer" onClick={() => handleOpenModelEditor(id)} role="presentation">
                        {loading ? (
                          <HStack spacing={2} flexWrap="wrap" py={0.5}>
                            <Skeleton height="32px" width="72px" borderRadius="md" />
                            <Skeleton height="32px" width="64px" borderRadius="md" />
                            <Skeleton height="32px" width="64px" borderRadius="md" />
                          </HStack>
                        ) : (
                          <Flex w="100%" align="center" justify="space-between" gap={2} flexWrap="wrap" py={0.5}>
                            <Box flexShrink={0} minW="min-content">
                              {metricHeaders.length > 0 && (
                                <CompactStatusChip
                                  icon={MdBarChart}
                                  value={metricHeaders.length}
                                  tooltipTitle={metricHeaders.length > 1 ? "Metrics" : "Metric"}
                                  names={metricHeaders.map((h) => h.name?.trim())}
                                  ariaLabel="Metrics on this model"
                                />
                              )}
                            </Box>
                            <HStack spacing={2} flexWrap="wrap" justify="flex-end" flex="1" minW={0}>
                              {benchmarkHeaders.length > 0 && (
                                <CompactStatusChip
                                  icon={PiTarget}
                                  value={benchmarkHeaders.length}
                                  tooltipTitle="Benchmarks"
                                  names={benchmarkHeaders.map((h) => h.name?.trim())}
                                  ariaLabel="Benchmarks on this model"
                                />
                              )}
                              {attributeHeaders.length > 0 && (
                                <CompactStatusChip
                                  icon={FaChartPie}
                                  value={attributeHeaders.length}
                                  tooltipTitle="Attributes"
                                  names={attributeHeaders.map((h) => h.name?.trim())}
                                  ariaLabel="Attribute links in knowledge graph"
                                />
                              )}
                              {measureHeaders.length > 0 && (
                                <CompactStatusChip
                                  icon={MdNumbers}
                                  value={measureHeaders.length}
                                  tooltipTitle="Measures"
                                  names={measureHeaders.map((h) => h.name?.trim())}
                                  ariaLabel="Measure links in knowledge graph"
                                />
                              )}
                              {temporalHeaders.length > 0 && (
                                <CompactStatusChip
                                  icon={FiCalendar}
                                  value={temporalHeaders.length}
                                  tooltipTitle="Datetime"
                                  names={temporalHeaders.map((h) => h.name?.trim())}
                                  ariaLabel="Datetime links in knowledge graph"
                                />
                              )}
                            </HStack>
                          </Flex>
                        )}
                      </Box>
                    </Flex>
                  </CardBody>
                </Card>
              );
            })
          ) : (
            <Box gridColumn="1 / -1">
              <Text color="gray.500" fontSize="sm">
                No data models yet. Create one from the ThoughtSpot flow or your admin tools.
              </Text>
            </Box>
          )}
        </SimpleGrid>
      </VStack>

      <Modal isOpen={deleteModal.isOpen} onClose={deleteModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          {dependentMetricHeaders.length > 0 ? (
            <>
              <ModalHeader fontSize="md">Cannot Delete Model</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text mb={2}>Please delete the following dependent metrics first:</Text>
                <Stack spacing={0}>
                  {dependentMetricHeaders.map((header, idx) => (
                    <Text key={idx} ml={4} mb={0}>
                      • {header.name}
                    </Text>
                  ))}
                </Stack>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant="ghost"
                  onClick={() => {
                    deleteModal.onClose();
                    setDependentMetricHeaders([]);
                  }}
                >
                  Close
                </Button>
              </ModalFooter>
            </>
          ) : (
            <>
              <ModalHeader fontSize="md">Delete Model Confirmation</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text>Are you sure you want to delete this model?</Text>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="purple" mr={3} onClick={confirmDelete}>
                  OK
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDeleteModelId(null);
                    deleteModal.onClose();
                  }}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ModelList;
